// src/components/modals/AddUserModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Stack,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  useMediaQuery,
  TextField,
  MenuItem,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha, useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";

import UserFormFields from "./UserFormFields";
import {
  defaultPreferences,
  defaultUserPermissions,
  normalizeUserId,
  userPermissionsSchema,
} from "./permissionsDefaults";

import { useCreateUserMutation, useEditUserMutation } from "../../redux/admin/adminApi";

/** ---------------------------
 * helpers
 * -------------------------- */
const Card = ({ title, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: 2,
      bgcolor: "background.paper",
    }}
  >
    <Stack spacing={1.25}>
      <Typography variant="subtitle1" fontWeight={800}>
        {title}
      </Typography>
      <Divider />
      {children}
    </Stack>
  </Paper>
);

const CollapsibleSection = ({ title, subtitle, expanded, onChange, children, defaultExpanded = false }) => (
  <Accordion
    disableGutters
    expanded={expanded}
    onChange={onChange}
    defaultExpanded={defaultExpanded}
    sx={(t) => ({
      borderRadius: 2,
      overflow: "hidden",
      border: `1px solid ${alpha(t.palette.divider, 0.85)}`,
      boxShadow: "none",
      "&:before": { display: "none" },
      bgcolor: alpha(t.palette.common.white, 0.98),
    })}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
      sx={(t) => ({
        minHeight: 48,
        px: 1.5,
        py: 0.25,
        bgcolor: alpha(t.palette.common.black, 0.03),
        borderBottom: expanded ? `1px solid ${alpha(t.palette.divider, 0.7)}` : "none",
        "& .MuiAccordionSummary-content": {
          my: 0.5,
        },
      })}
    >
      <Box>
        <Typography fontWeight={800} fontSize="0.96rem">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</AccordionDetails>
  </Accordion>
);

const setDeep = (obj, path, value) => {
  const keys = path.split(".");
  const out = structuredClone(obj);
  let cur = out;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = cur[keys[i]] ?? {};
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return out;
};

const ATTRIBUTE_OPTIONS = [
  { key: "notificationTokens", label: "Notification Tokens", type: "number" },
  { key: "currency", label: "Currency", type: "select", options: ["INR", "USD"] },
  { key: "unitPrice", label: "Unit Price", type: "number" },
  { key: "managerEmailAddress", label: "Manager Email Address", type: "email" },
  { key: "managerContactNumber", label: "Manager Contact Number", type: "tel" },
  { key: "remarks", label: "Remarks", type: "text" },
  { key: "roleAttribute", label: "Role", type: "select", options: ["Parents", "Subuser", "Relay hide"] },
  { key: "video", label: "Video", type: "checkbox" },
];

const ATTRIBUTE_MAP = ATTRIBUTE_OPTIONS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

/**
 * parentAllowed: schema ke hisaab se allowed bools nikalta hai
 * - agar currentUser.permissions exist ho -> use it
 * - warna devicePermissions/userPermissions se fallback mapping
 */
const buildParentAllowedForUserSchema = (currentUser) => {
  const nestedPermissions = currentUser?.permissions;
  if (
    nestedPermissions &&
    typeof nestedPermissions === "object" &&
    nestedPermissions.user
  ) {
    return currentUser.permissions;
  }

  const app = currentUser?.permissions?.app || {};

  return {
    user: {
      hideValidity:
        currentUser?.userPermissions?.create === true ||
        currentUser?.userPermissions?.update === true ||
        app.createUser === true ||
        app.editUser === true,
    },
  };
};

/**
 * schema-based clamp:
 * - parent ke paas false -> child forced false
 * - parent true -> child apne selection ke hisaab se true/false
 */
const clampBySchema = (schema, parentAllowed, selected) => {
  const out = structuredClone(selected || {});
  schema.forEach((group) => {
    out[group.key] = out[group.key] ?? {};
    const parentGroup = parentAllowed?.[group.key] || {};
    group.items.forEach((it) => {
      const isAllowed = parentGroup?.[it.key] === true;
      out[group.key][it.key] = isAllowed ? Boolean(out[group.key]?.[it.key]) : false;
    });
  });
  return out;
};

const buildGeneratedUserEmail = (form, existingEmail) => {
  if (existingEmail) return existingEmail;
  const seed = normalizeUserId(form.uniqueId || form.username || form.mobileNo || "user");
  return `${seed || "user"}-${Date.now()}@user.local`;
};

const toUserFormState = (user) => {
  if (!user) {
    return {
      username: "",
      email: "",
      mobileNo: "",
      password: "",
      uniqueId: "",
      preferences: structuredClone(defaultPreferences),
      permissions: structuredClone(defaultUserPermissions),
      attributes: [],
    };
  }

  return {
    username: user.username || "",
    email: user.email || "",
    mobileNo: user.mobileNo || "",
    password: "",
    uniqueId: user.uniqueId || "",
    preferences: {
      ...defaultPreferences,
      ...(user.preferences || {}),
      timeZone:
        user.preferences?.timeZone ||
        user.preferences?.timezone ||
        defaultPreferences.timeZone,
      language: user.preferences?.language || defaultPreferences.language,
    },
    permissions: user.permissions
      ? structuredClone(user.permissions)
      : structuredClone(defaultUserPermissions),
    attributes: Array.isArray(user.attributes) ? structuredClone(user.attributes) : [],
  };
};

export default function AddUserModal({
  open,
  onClose,
  onSuccess,
  mode = "create",
  initialData = null,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [createUser, { isLoading }] = useCreateUserMutation();
  const [editUser, { isLoading: isEditing }] = useEditUserMutation();

  // ✅ current logged-in user (parent)
  const currentUser = useSelector((s) => s?.auth?.user);

  const parentAllowed = useMemo(
    () => buildParentAllowedForUserSchema(currentUser),
    [currentUser]
  );

  const [error, setError] = useState("");

  // ✅ FIX: fields aligned with your backend payload style
  const [form, setForm] = useState(() => toUserFormState(initialData));
  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [selectedAttributeKey, setSelectedAttributeKey] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    permissions: false,
    attributes: false,
  });

  useEffect(() => {
    if (!open) return;
    setForm(toUserFormState(initialData));
    setError("");
    setAttributeDialogOpen(false);
    setSelectedAttributeKey("");
    setExpandedSections({
      details: true,
      permissions: false,
      attributes: false,
    });
  }, [open, initialData]);

  const toggleSection = (key) => (_, nextExpanded) => {
    setExpandedSections((prev) => ({ ...prev, [key]: nextExpanded }));
  };

  const onChange = (path, value) => {
    setError("");
    // if you want normalize on uniqueId
    if (path === "uniqueId") value = normalizeUserId(value);
    setForm((prev) => setDeep(prev, path, value));
  };

  const togglePermission = (groupKey, itemKey) => {
    setError("");
    const can = parentAllowed?.[groupKey]?.[itemKey] === true;
    if (!can) return; // ✅ cannot toggle if parent doesn't have it

    setForm((prev) => {
      const current = !!prev.permissions?.[groupKey]?.[itemKey];
      return setDeep(prev, `permissions.${groupKey}.${itemKey}`, !current);
    });
  };

  const handleSubmit = async () => {
    setError("");

    if (!form.username?.trim()) return setError("Username is required");
    if (mode !== "edit" && !form.password?.trim()) return setError("Password is required");

    // ✅ clamp permissions before sending
    const clampedPermissions = clampBySchema(
      userPermissionsSchema,
      parentAllowed,
      form.permissions
    );

    const payload = {
      username: form.username.trim(),
      email: buildGeneratedUserEmail(form, initialData?.email || form.email).toLowerCase(),
      mobileNo: String(form.mobileNo || "").trim(),
      ...(form.password?.trim() ? { password: form.password } : {}),
      role: "user",
      uniqueId: form.uniqueId ? normalizeUserId(form.uniqueId) : undefined,

      preferences: form.preferences,
      permissions: clampedPermissions,
      attributes: form.attributes,
    };

    try {
      const res =
        mode === "edit" && initialData?._id
          ? await editUser({ userId: initialData._id, ...payload }).unwrap()
          : await createUser(payload).unwrap();
      onSuccess?.(res);
      onClose?.();

      setForm(toUserFormState(null));
      setAttributeDialogOpen(false);
      setSelectedAttributeKey("");
    } catch (e) {
      setError(
        e?.data?.msg ||
          e?.message ||
          (mode === "edit" ? "Failed to update user" : "Failed to create user")
      );
    }
  };

  const availableAttributeOptions = ATTRIBUTE_OPTIONS.filter(
    (item) => !(form.attributes || []).some((attr) => attr.key === item.key)
  );

  const addAttribute = () => {
    if (!selectedAttributeKey) return;
    const selectedConfig = ATTRIBUTE_MAP[selectedAttributeKey];
    if (!selectedConfig) return;

    setForm((prev) => ({
      ...prev,
      attributes: [
        ...(prev.attributes || []),
        {
          key: selectedConfig.key,
          label: selectedConfig.label,
          type: selectedConfig.type,
          value: selectedConfig.type === "checkbox" ? false : "",
        },
      ],
    }));
    setSelectedAttributeKey("");
    setAttributeDialogOpen(false);
  };

  const updateAttribute = (index, value) => {
    setForm((prev) => {
      const nextAttributes = [...(prev.attributes || [])];
      nextAttributes[index] = { ...nextAttributes[index], value };
      return { ...prev, attributes: nextAttributes };
    });
  };

  const removeAttribute = (index) => {
    setForm((prev) => ({
      ...prev,
      attributes: (prev.attributes || []).filter((_, attrIndex) => attrIndex !== index),
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          boxShadow: `0 16px 44px ${alpha(theme.palette.common.black, 0.2)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={900}>
            {mode === "edit" ? "Edit User" : "Add User"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {mode === "edit"
              ? "Update user information, permissions and attributes"
              : "Create user account with exact permissions and attributes"}
          </Typography>
        </Box>

        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "grey.50" }}>
        <Stack spacing={2.25}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <CollapsibleSection
            title="User details"
            subtitle="Required account fields and preferences"
            expanded={expandedSections.details}
            onChange={toggleSection("details")}
            defaultExpanded
          >
            <UserFormFields
              form={form}
              onChange={onChange}
              showEmail={false}
              requirePassword={mode !== "edit"}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Permissions"
            subtitle="Parent-limited user permissions"
            expanded={expandedSections.permissions}
            onChange={toggleSection("permissions")}
          >
            <Card title="Permissions">
              {userPermissionsSchema.map((group) => (
                <Stack key={group.key} spacing={1}>
                  {group.items.map((it) => {
                    const allowed = parentAllowed?.[group.key]?.[it.key] === true;
                    const checked = !!form.permissions?.[group.key]?.[it.key];

                    return (
                      <FormControlLabel
                        key={it.key}
                        disabled={!allowed}
                        control={
                          <Checkbox
                            checked={allowed ? checked : false}
                            onChange={() => togglePermission(group.key, it.key)}
                          />
                        }
                        label={
                          <Typography variant="body2" color={!allowed ? "text.disabled" : "text.primary"}>
                            {it.label}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    );
                  })}
                </Stack>
              ))}
            </Card>
          </CollapsibleSection>

          <CollapsibleSection
            title="Attributes"
            subtitle={`${(form.attributes || []).length} attributes added`}
            expanded={expandedSections.attributes}
            onChange={toggleSection("attributes")}
          >
            <Card title="Attributes">
              <Stack spacing={1.5}>
                {(form.attributes || []).map((attr, index) => {
                  const config = ATTRIBUTE_MAP[attr.key] || attr;

                  return (
                    <Stack key={attr.key} direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        {config.type === "checkbox" ? (
                          <FormControlLabel
                            sx={{ m: 0, mb: 0.5 }}
                            control={
                              <Checkbox
                                checked={Boolean(attr.value)}
                                onChange={(e) => updateAttribute(index, e.target.checked)}
                              />
                            }
                            label={config.label || attr.key}
                          />
                        ) : (
                          <Typography sx={{ mb: 0.75, fontWeight: 500 }}>
                            {config.label || attr.key}
                          </Typography>
                        )}

                        {config.type === "select" ? (
                          <TextField
                            select
                            value={attr.value || ""}
                            onChange={(e) => updateAttribute(index, e.target.value)}
                            size="small"
                            fullWidth
                          >
                            {(config.options || []).map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : config.type === "checkbox" ? null : (
                          <TextField
                            type={config.type || "text"}
                            value={attr.value || ""}
                            onChange={(e) => updateAttribute(index, e.target.value)}
                            size="small"
                            fullWidth
                            placeholder={config.key === "currency" ? "Select Currency" : ""}
                          />
                        )}
                      </Box>

                      <IconButton color="error" onClick={() => removeAttribute(index)} size="small">
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  );
                })}

                <Button
                  variant="outlined"
                  onClick={() => setAttributeDialogOpen(true)}
                  disabled={!availableAttributeOptions.length}
                  sx={{ fontWeight: 700, py: 1.1 }}
                >
                  + ADD
                </Button>
              </Stack>
            </Card>
          </CollapsibleSection>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          User role: <b>user</b>
        </Typography>

        <Stack direction="row" spacing={1.25}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || isEditing}
            sx={{ minWidth: 140, fontWeight: 800 }}
          >
            {isLoading || isEditing ? "Saving..." : mode === "edit" ? "Update" : "Save"}
          </Button>
        </Stack>
      </DialogActions>

      <MuiDialog
        open={attributeDialogOpen}
        onClose={() => setAttributeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <MuiDialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.5}>
            <TextField
              select
              label="Attribute"
              value={selectedAttributeKey}
              onChange={(e) => setSelectedAttributeKey(e.target.value)}
              size="small"
              fullWidth
            >
              {availableAttributeOptions.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Type"
              value={selectedAttributeKey ? ATTRIBUTE_MAP[selectedAttributeKey]?.type || "" : ""}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Stack>
        </MuiDialogContent>

        <MuiDialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={addAttribute} disabled={!selectedAttributeKey}>
            Add
          </Button>
          <Button onClick={() => setAttributeDialogOpen(false)}>Cancel</Button>
        </MuiDialogActions>
      </MuiDialog>
    </Dialog>
  );
}
